import React, { useCallback, useMemo } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import CategoryIcon from '@mui/icons-material/Category';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import type { EquipmentSummary } from '@api/bff/equipment/useEquipment';

type MetadataItemProps = {
  icon: React.ElementType;
  children: React.ReactNode;
};

const MetadataItem = ({ icon: Icon, children }: MetadataItemProps) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
    <Icon sx={{ fontSize: 16, color: 'text.secondary' }} />
    <Typography variant="body2" color="text.secondary" noWrap>
      {children}
    </Typography>
  </Box>
);

type Props = {
  equipment: EquipmentSummary;
  isSelected: boolean;
  onSelect: (equipment: EquipmentSummary) => void;
};

export const EquipmentCard = React.memo<Props>(
  ({ equipment, isSelected, onSelect }) => {
    const manufacturerModel = useMemo(
      () =>
        [equipment.manufacturer, equipment.modelNumber]
          .filter(Boolean)
          .join(' '),
      [equipment.manufacturer, equipment.modelNumber]
    );

    const showType =
      equipment.type && equipment.type.toLowerCase() !== 'scale';

    const handleClick = useCallback(() => {
      onSelect(equipment);
    }, [onSelect, equipment]);

    return (
      <Card
        variant="outlined"
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s',
          borderColor: isSelected ? 'primary.main' : undefined,
          borderWidth: isSelected ? 2 : 1,
          '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
          '&:active': { transform: 'translateY(0)' }
        }}
        onClick={handleClick}
      >
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap'
            }}
          >
            <MetadataItem icon={BadgeIcon}>
              <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                Asset ID
              </Box>
              <Box
                component="span"
                sx={{ color: 'primary.main', fontWeight: 'bold' }}
              >
                {equipment.id}
              </Box>
            </MetadataItem>

            {manufacturerModel && (
              <Typography
                variant="subtitle2"
                color="text.primary"
                noWrap
                sx={{ flexShrink: 0 }}
              >
                {manufacturerModel}
              </Typography>
            )}

            {showType && (
              <MetadataItem icon={CategoryIcon}>{equipment.type}</MetadataItem>
            )}

            {equipment.serialNumber && (
              <MetadataItem icon={FingerprintIcon}>
                SN: {equipment.serialNumber}
              </MetadataItem>
            )}
          </Box>

          {equipment.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ mt: 0.25 }}
            >
              {equipment.description}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }
);

EquipmentCard.displayName = 'EquipmentCard';
